/**
 * Camera Kit Web SDK implementation of the AR module contract.
 *
 * Performance rules for the always-on Lens path:
 * - WASM + Lens are warmed earlier via warmCameraKit() (title / attract).
 * - Prefer an already-open camera stream from the permission step — do not
 *   call getUserMedia a second time.
 * - play() and applyLens() run as soon as the source is set.
 * - Mouth-open uses local MediaPipe (same stream) → config.onTrigger().
 */

import { createMediaStreamSource, Transform2D } from '@snap/camera-kit';
import type { CameraKit, Lens, CameraKitSession } from '@snap/camera-kit';
import {
  invalidateCameraKitWarm,
  refreshCameraKitLens,
  warmCameraKit,
} from './cameraKitWarm';
import { createMouthOpenDetector, type MouthOpenDetector } from './mouthOpenDetector';
import { AR_CANVAS_HEIGHT, AR_CANVAS_WIDTH, type ARConfig, type ARModule } from './types';
import { devError, devLog, devWarn } from '../lib/devLog';

const TAG = '[ar:camera-kit]';

function log(call: string, ...rest: unknown[]): void {
  devLog(`${TAG} ${call}`, ...rest);
}

/** CDN / package integrity failures that often succeed on a single re-download. */
function isRetryableLensError(err: unknown): boolean {
  const name = err instanceof Error ? err.name : '';
  const message = err instanceof Error ? err.message : String(err);
  const blob = `${name} ${message}`.toLowerCase();
  return (
    name === 'LensContentValidationError' ||
    name === 'LensCoreValidationError' ||
    blob.includes('checksum') ||
    blob.includes('content validation') ||
    blob.includes('failed to apply lens')
  );
}

function toError(err: unknown, fallback: string): Error {
  if (err instanceof Error) return err;
  return new Error(fallback);
}

export function createCameraKitModule(): ARModule {
  let session: CameraKitSession | null = null;
  let preloadedLens: Lens | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let cameraStream: MediaStream | null = null;
  /** True when we opened the camera ourselves and must stop it in destroy(). */
  let ownsCamera = false;
  let destroyed = false;
  let mouth: MouthOpenDetector | null = null;
  let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
  let cfg: ARConfig | null = null;
  let triggered = false;
  /** Avoid double ErrorScreen from session event + thrown applyLens. */
  let errorReported = false;

  function fireTrigger(source: string): void {
    if (destroyed || triggered) return;
    triggered = true;
    log(`onTrigger (${source})`);
    cfg?.onTrigger();
  }

  function reportError(err: Error): void {
    if (destroyed || errorReported) return;
    errorReported = true;
    log('onError', err.name, err.message);
    cfg?.onError(err);
  }

  async function playAndApplyLens(
    active: CameraKitSession,
    cameraKit: CameraKit,
    lens: Lens,
  ): Promise<Lens> {
    try {
      await Promise.all([active.play('live'), active.applyLens(lens)]);
      return lens;
    } catch (err) {
      if (destroyed || !isRetryableLensError(err)) throw err;

      log('applyLens failed — refreshing lens and retrying once', toError(err, 'applyLens').message);
      invalidateCameraKitWarm();
      const refreshed = await refreshCameraKitLens(cameraKit);
      if (destroyed) return refreshed.lens;

      // play() may already be live from the first attempt.
      if (!active.playing.live) await active.play('live');
      await active.applyLens(refreshed.lens);
      return refreshed.lens;
    }
  }

  return {
    async init(container: HTMLElement, config: ARConfig): Promise<void> {
      log('init', {
        deviceId: config.deviceId ?? '(default)',
        reusedCamera: Boolean(config.videoStream),
      });
      destroyed = false;
      triggered = false;
      errorReported = false;
      cfg = config;
      const t0 = performance.now();

      // Step timings. Init is seconds on a real phone and near-instant on a
      // desktop, so the only way to know which step costs it is to measure on
      // the device that is slow. Cheap enough to leave in.
      let mark = t0;
      const step = (label: string): void => {
        const now = performance.now();
        log(`  ${label} ${Math.round(now - mark)}ms`);
        mark = now;
      };

      // 1. Use the warm cache — bootstrap + lens download should already be done.
      let cameraKit: CameraKit;
      let lens: Lens;
      try {
        ({ cameraKit, lens: preloadedLens } = await warmCameraKit());
        lens = preloadedLens;
        step('warm');
      } catch (err) {
        reportError(toError(err, 'Camera Kit warm failed'));
        return;
      }

      if (destroyed) return;

      /*
       * 2. Create session — Camera Kit creates the canvas for us.
       *
       * `renderWhileTabHidden` is set for what it does as a side effect:
       * `createSession` computes `shouldUseWorker: !renderWhileTabHidden &&
       * config.shouldUseWorker`, and `shouldUseWorker` defaults to TRUE. So
       * without this the lens runs in a Web Worker on an OffscreenCanvas, and
       * with it on the main thread.
       *
       * Both render the costume. Only the main thread renders PixelChefs'
       * smoke: the kiosk's render machine has always passed this flag and
       * smokes, the web flow never did and does not (2026-09-20). The lens is
       * the same package, byte for byte, in both.
       *
       * The literal meaning of the flag costs little here. The capture screen
       * is on screen for seconds and unmounts on the way out, which destroys
       * the session — so there is no lens quietly rendering behind a
       * backgrounded tab.
       */
      session = await cameraKit.createSession({ renderWhileTabHidden: true });
      step('createSession (main thread — see the note above)');

      if (destroyed) {
        await session.destroy();
        session = null;
        return;
      }

      // 3. Mount the live canvas.
      canvas = session.output.live;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.objectFit = 'cover';
      canvas.style.display = 'block';
      container.appendChild(canvas);

      // Any unrecoverable session error (incl. LensAbortError) → ErrorScreen.
      session.events.addEventListener('error', (event) => {
        if (destroyed) return;
        const detail = event.detail.error;
        devError(`${TAG} session error`, detail);
        reportError(
          detail instanceof Error
            ? detail
            : new Error(`Camera Kit session error: ${String(detail)}`),
        );
      });

      // Mute Lens audio — we own all audio.
      session.mute();

      // 4. Camera: reuse the permission stream when available, otherwise open one.
      if (config.videoStream && config.videoStream.getVideoTracks().some((t) => t.readyState === 'live')) {
        cameraStream = config.videoStream;
        ownsCamera = false;
        log('reusing permission camera stream');
      } else {
        // Portrait on the open-a-camera-ourselves path, to match what
        // requestPermissions asks for and what the 9:16 compositor crops to.
        // The configured-device branch is the kiosk's fixed camera and is left
        // as it was — the crop handles its shape either way.
        // On the kiosk the facingMode branch below is a hardware hazard rather
        // than a convenience: no camera on that panel reports as "user", so the
        // ideal constraint is dropped and Chromium opens the default device —
        // which may be the RK628 HDMI bridge that kernel-panics the board. The
        // kiosk sets allowDefaultCamera:false and we refuse instead.
        if (!config.deviceId && config.allowDefaultCamera === false) {
          reportError(
            new Error('no camera device id resolved — refusing to open a default camera'),
          );
          return;
        }

        const videoConstraints: MediaTrackConstraints = config.deviceId
          ? { deviceId: { exact: config.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : {
              facingMode: 'user',
              width: { ideal: AR_CANVAS_WIDTH },
              height: { ideal: AR_CANVAS_HEIGHT },
              aspectRatio: { ideal: AR_CANVAS_WIDTH / AR_CANVAS_HEIGHT },
            };

        try {
          cameraStream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: false,
          });
          ownsCamera = true;
          log('opened new camera stream');
        } catch (err) {
          reportError(toError(err, 'Camera open failed'));
          return;
        }
      }

      if (destroyed) {
        if (ownsCamera) cameraStream?.getTracks().forEach((t) => t.stop());
        cameraStream = null;
        return;
      }

      // 5. Attach source, play, apply Lens — one automatic retry on CDN checksum fails.
      //
      // `fpsLimit` matters more than it looks on the kiosk. Camera Kit's default
      // is POSITIVE_INFINITY, which reaches LensCore as "no limit", so the lens
      // renders as fast as the GPU will let it — against a camera delivering 30.
      // On the Mali-G52 in the RK3568 that starves everything sharing the
      // device, including this page's own rAF loop.
      //
      // Measured on the unit 2026-09-18, live capture screen, with NO recorder
      // running at all: 60fps while the AR canvas was still a 300x150
      // placeholder, collapsing to 6-11fps in the same second the 720x1280
      // canvases appeared and the lens began rendering. The encoder was blamed
      // for this first and was not responsible.
      //
      // There is nothing above the camera's own rate to win, so ask for its
      // rate. Passed at the source rather than via `session.setFPSLimit()` so it
      // applies on attach rather than as a second awaited call that could fail
      // after the source is already live.
      const source = createMediaStreamSource(cameraStream!, {
        transform: Transform2D.MirrorX,
        cameraType: 'user',
        ...(config.fpsLimit ? { fpsLimit: config.fpsLimit } : {}),
      });
      await session.setSource(source);
      step('setSource');

      if (destroyed) return;

      try {
        /*
         * Benchmark escape hatch: run the session with NO lens.
         *
         * Set VITE_CK_NO_LENS=1 to play the camera through LensCore without
         * applying anything on top. It is the control for "is the live camera
         * slow because of the lens, or because of Camera Kit on this GPU?" —
         * a question no amount of measuring the app itself can answer, because
         * every other suspect was already ruled out (docs/KIOSK_PERFORMANCE.md).
         *
         * Not a product mode: with no lens there is no costume, so the guest
         * sees only themselves. Never ship with this set.
         */
        if (import.meta.env.VITE_CK_NO_LENS === '1') {
          await session.play('live');
          step('play (NO LENS — benchmark mode)');
          devLog(`${TAG} running without a lens: benchmark only`);
        } else {
          lens = await playAndApplyLens(session, cameraKit, lens);
          preloadedLens = lens;
          step('playAndApplyLens');
        }
      } catch (err) {
        // Drop warm cache so ErrorScreen → Retry re-downloads instead of reusing a bad package.
        invalidateCameraKitWarm();
        reportError(toError(err, 'Failed to apply Lens'));
        return;
      }

      if (destroyed) return;

      // 6. Local MediaPipe mouth-open on the same camera stream.
      mouth = createMouthOpenDetector();
      try {
        await mouth.start(cameraStream!, () => fireTrigger('mouth'));
      } catch (err) {
        // Detection failure must not block the Lens — spacebar / fallback still work.
        devWarn(`${TAG} mouth detector failed to start`, err);
        mouth.stop();
        mouth = null;
      }

      // Dev fallback — same as the stub.
      onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'Space') return;
        e.preventDefault();
        fireTrigger('spacebar');
      };
      window.addEventListener('keydown', onKeyDown);

      log(`ready in ${Math.round(performance.now() - t0)}ms`);
      config.onReady();
    },

    start(): void {
      log('start');
      if (!session) return;
      if (!session.playing.live) void session.play('live');
    },

    stop(): void {
      log('stop');
      mouth?.stop();
      if (!session) return;
      void session.pause('live');
    },

    setCostumeVisible(_visible: boolean): void {
      log('setCostumeVisible (no-op — always-on mode)', _visible);
    },

    getCanvas(): HTMLCanvasElement {
      log('getCanvas');
      if (!canvas) throw new Error('Camera Kit: getCanvas() before init()');
      return canvas;
    },

    destroy(): void {
      log('destroy');
      destroyed = true;
      cfg = null;

      mouth?.stop();
      mouth = null;

      if (onKeyDown) {
        window.removeEventListener('keydown', onKeyDown);
        onKeyDown = null;
      }

      if (ownsCamera) {
        cameraStream?.getTracks().forEach((t) => t.stop());
      }
      cameraStream = null;
      ownsCamera = false;

      if (session) {
        void session.destroy();
        session = null;
      }

      canvas?.parentElement?.removeChild(canvas);
      canvas = null;
      preloadedLens = null;
      triggered = false;
    },
  };
}
